import type { TabsProps } from 'antd';
import { Tabs } from 'antd';
import useBreakpoint from 'antd/lib/grid/hooks/useBreakpoint';
import clsx from 'clsx';
import { GetServerSideProps } from 'next';
import { useRouter } from 'next/router';
import { renderToStaticMarkup } from 'react-dom/server';

import CookiesPolicy from '@/components/Policies/CookiesPolicy';
import PrivacyPolicy from '@/components/Policies/PrivacyPolicy';
import RefundPolicy from '@/components/Policies/RefundPolicy';
import TermsOfUse from '@/components/Policies/TermsOfUse';

const policyTabs = [
  { label: 'Terms & Conditions', slug: 'terms-and-conditions' },
  { label: 'Privacy Policy', slug: 'privacy-policy' },
  { label: 'Refund Policy', slug: 'refund-policy' },
  { label: 'Cookie Policy', slug: 'cookie-policy' },
];

interface PoliciesPageProps {
  activeSlug: string;
  policyHTML: string;
}

export default function PoliciesPage({
  activeSlug,
  policyHTML,
}: PoliciesPageProps) {
  const router = useRouter();
  const screens = useBreakpoint();

  const handleTabChange = (key: string) => {
    if (key !== activeSlug) {
      router.push(`/policies/${key}`, undefined, { scroll: false });
    }
  };

  const items: TabsProps['items'] = policyTabs.map((tab) => ({
    key: tab.slug,
    label: (
      <span
        className={clsx(
          activeSlug === tab.slug
            ? 'text-white'
            : 'text-gray-600 dark:text-gray-300',
          'text-sm md:text-lg font-medium px-2 md:px-4 transition-colors duration-300',
        )}
      >
        {tab.label}
      </span>
    ),
  }));

  return (
    <div className='min-h-screen transition-colors duration-300'>
      <div className='max-w-5xl mx-auto px-4 py-10 sm:py-16 md:py-20'>
        <div className='overflow-x-auto'>
          <Tabs
            activeKey={activeSlug}
            onChange={handleTabChange}
            items={items}
            tabBarGutter={screens.md ? 20 : 12}
            centered
            tabBarStyle={{
              borderBottom: '1px solid rgba(107, 114, 128, 0.2)',
              marginBottom: 0,
            }}
            className='policy-tabs'
          />
        </div>

        <div
          className='mt-8 leading-relaxed'
          dangerouslySetInnerHTML={{ __html: policyHTML }}
        />
      </div>
    </div>
  );
}

export const getServerSideProps: GetServerSideProps = async (context) => {
  const { slug } = context.params || {};
  const validSlugs = policyTabs.map((tab) => tab.slug);
  const activeSlug =
    typeof slug === 'string' && validSlugs.includes(slug)
      ? slug
      : 'privacy-policy';

  let policyHTML = '';

  switch (activeSlug) {
    case 'terms-and-conditions':
      policyHTML = renderToHTML(<TermsOfUse />);
      break;
    case 'refund-policy':
      policyHTML = renderToHTML(<RefundPolicy />);
      break;
    case 'cookie-policy':
      policyHTML = renderToHTML(<CookiesPolicy />);
      break;
    default:
      policyHTML = renderToHTML(<PrivacyPolicy />);
      break;
  }

  return {
    props: { activeSlug, policyHTML },
  };
};

function renderToHTML(component: React.ReactElement) {
  return renderToStaticMarkup(component);
}
